import styled, { keyframes, css } from 'styled-components';
import theme from '../../theme';

const rise = keyframes`
  from { opacity: 0; transform: translateY(28px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const pulse = keyframes`
  0%,100% { opacity:0.3; transform:scale(0.8); }
  50%     { opacity:1;   transform:scale(1);   }
`;

const appear = (delay = 0) => css`
  opacity: 0;
  animation: ${rise} 0.75s ${delay}s cubic-bezier(0.16, 1, 0.3, 1) forwards;
`;

const MONO = theme.font.mono;

// ── Page ──────────────────────────────────────────────────────────────────────

export const PageWrap = styled.div`
  min-height: 100vh;
  background: ${theme.dossier.bg};
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  -webkit-font-smoothing: antialiased;
`;

/* Mask under iOS-statusbaren så skrollande text tonar ut istället för att
   krocka med klockan — Apple-mönstret för mörka sidor. */
export const TopFade = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0;
  z-index: 10;
  height: calc(env(safe-area-inset-top, 0px) + 28px);
  background: linear-gradient(to bottom, rgba(5,11,9,0.94) 0%, rgba(5,11,9,0) 100%);
  pointer-events: none;
`;

// ── Hero ──────────────────────────────────────────────────────────────────────

export const HeaderBar = styled.div`
  background: ${theme.dossier.bg};
  padding: calc(76px + env(safe-area-inset-top, 0px)) 28px 96px;
  text-align: center;
  position: relative;
  overflow: hidden;

  @media (min-width: 768px) {
    padding: calc(130px + env(safe-area-inset-top, 0px)) 28px 150px;
  }

  /* Tunn brand-keyline i absoluta toppen */
  &::after {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 2px;
    background: ${theme.dossier.keyline};
    opacity: 0.85;
  }

  /* Aurora — px-bundna ljuskällor (procent-ellipser bandar på breda skärmar) */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: ${theme.dossier.aurora};
    pointer-events: none;
  }
`;

export const HeaderInner = styled.div`
  position: relative;
`;

export const BrandMark = styled.div`
  font-family: ${MONO};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.42em;
  text-indent: 0.42em; /* kompenserar sista bokstavens spacing vid centrering */
  color: ${theme.dossier.tealBright};
  margin-bottom: 18px;
  ${appear(0)}
`;

export const ConfidentialLabel = styled.div`
  font-family: ${MONO};
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.30em;
  text-indent: 0.30em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.46);
  margin-bottom: 48px;
  ${appear(0.05)}
`;

export const CompanyName = styled.h1`
  font-family: 'Playfair Display', Georgia, serif;
  font-size: clamp(52px, 14vw, 76px);
  font-weight: 700;
  margin: 0 0 24px;
  line-height: 1.04;
  letter-spacing: -0.03em;

  /* Apple-metallisk text: vit som tonar mot teal-is i botten */
  color: ${theme.dossier.inkOnDark}; /* fallback när background-clip saknas */
  background: ${theme.dossier.metallicText};
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;

  ${appear(0.10)}
`;

export const MetaLine = styled.div`
  font-size: 15px;
  color: rgba(255,255,255,0.58);
  display: flex;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
  ${appear(0.17)}
`;

export const MetaDot = styled.span`
  color: rgba(93,214,202,0.45);
`;

export const HeaderDate = styled.div`
  font-family: ${MONO};
  font-size: 11px;
  letter-spacing: 0.14em;
  color: rgba(255,255,255,0.36);
  margin-top: 32px;
  ${appear(0.24)}
`;

// ── Signal / finding section ──────────────────────────────────────────────────

export const SignalSection = styled.div`
  background: ${theme.dossier.bg};
  border-top: 1px solid rgba(255,255,255,0.07);
  padding: 84px 28px 76px;
  text-align: center;

  @media (min-width: 768px) {
    padding: 120px 28px 110px;
  }
`;

export const SectionEyebrow = styled.div`
  font-family: ${MONO};
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.30em;
  text-indent: 0.30em;
  text-transform: uppercase;
  color: ${theme.dossier.teal};
  margin-bottom: 40px;
`;

export const SignalCard = styled.div`
  margin-bottom: 44px;
  &:last-of-type { margin-bottom: 0; }
  ${({ $i }) => appear(0.08 + ($i ?? 0) * 0.06)}
`;

export const SignalBullet = styled.div`
  width: 36px;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(93,214,202,0.7), transparent);
  margin: 0 auto 30px;
`;

export const SignalText = styled.p`
  font-family: 'Playfair Display', Georgia, serif;
  font-size: clamp(24px, 6.4vw, 33px);
  font-weight: 500;
  color: ${theme.dossier.inkOnDark};
  line-height: 1.46;
  max-width: 560px;
  margin: 0 auto;
  letter-spacing: -0.012em;
`;

export const DataCard = styled.div`
  margin: 52px auto 0;
  max-width: 360px;
  border: 1px solid rgba(255,255,255,0.10);
  border-radius: 16px;
  background: rgba(255,255,255,0.025);
  padding: 22px 24px;
  display: flex;
  flex-direction: column;
  gap: 15px;
  text-align: left;
`;

export const DataRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
`;

export const DataDesc = styled.span`
  font-size: 13px;
  color: rgba(255,255,255,0.48);
`;

export const DataVal = styled.span`
  font-family: ${MONO};
  font-size: 12.5px;
  font-weight: 500;
  color: ${({ $highlight }) => $highlight ? theme.dossier.tealBright : 'rgba(255,255,255,0.88)'};
  text-align: right;
`;

// ── Financial section ─────────────────────────────────────────────────────────

export const FinancialSection = styled.div`
  background: ${theme.dossier.bg};
  border-top: 1px solid rgba(255,255,255,0.07);
  padding: 96px 28px 92px;
  text-align: center;
  position: relative;
  overflow: hidden;

  @media (min-width: 768px) {
    padding: 130px 28px 126px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    width: 520px; height: 360px;
    background: radial-gradient(ellipse, rgba(43,196,172,0.12) 0%, transparent 65%);
    pointer-events: none;
  }
`;

export const BigNumber = styled.div`
  position: relative;
  font-size: clamp(58px, 16.5vw, 92px);
  font-weight: 800;
  letter-spacing: -0.05em;
  line-height: 1;

  color: ${theme.dossier.teal};
  background: ${theme.dossier.numberGradient};
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;

  ${appear(0.06)}
`;

export const BigNumberApprox = styled.span`
  font-size: 0.50em;
  font-weight: 600;
  vertical-align: 0.34em;
  margin-right: 0.10em;
`;

/* Intervallet som spektrum — precision man kan SE, inte tre rader text */
export const RangeWrap = styled.div`
  max-width: 320px;
  margin: 40px auto 0;
  ${appear(0.14)}
`;

export const RangeTrack = styled.div`
  position: relative;
  height: 3px;
  border-radius: 2px;
  background: linear-gradient(90deg, rgba(43,196,172,0.18) 0%, rgba(43,196,172,0.55) 50%, rgba(43,196,172,0.18) 100%);
`;

export const RangeMarker = styled.div`
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 11px; height: 11px;
  border-radius: 50%;
  background: ${theme.dossier.tealBright};
  box-shadow: ${theme.dossier.glow};
`;

export const RangeLabels = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 12px;
  font-family: ${MONO};
  font-size: 11px;
  letter-spacing: 0.04em;
  color: rgba(255,255,255,0.52);
`;

export const BigNumberSub = styled.div`
  font-size: 13px;
  color: rgba(255,255,255,0.50);
  margin-top: 30px;
  ${appear(0.18)}
`;

export const BigNumberNote = styled.div`
  font-size: 12px;
  font-style: italic;
  color: rgba(255,255,255,0.38);
  margin-top: 10px;
`;

// ── Estimate cards — RUMMETS mörka instrumentspråk (grundarbeslut 2026-07-02:
// gränssnittet identiskt rummet; den ljusa kontrastsektionen utgick) ─────────────

export const ContentArea = styled.div`
  background: ${theme.dossier.bg};
  padding: 56px 20px 48px;

  @media (min-width: 768px) {
    padding: 88px 28px 76px;
  }
`;

export const BreakdownEyebrow = styled.div`
  font-family: ${MONO};
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.30em;
  text-indent: 0.30em;
  text-transform: uppercase;
  color: ${theme.dossier.teal};
  margin-bottom: 26px;
  text-align: center;
`;

/* Dossier-kolumn — ett memo är aldrig bredare än läsbart, oavsett skärm */
export const EstimateCard = styled.div`
  background: ${theme.dossier.bgRaised};
  border: 1px solid ${theme.dossier.hairlineOnDark};
  border-radius: 20px;
  padding: 28px 24px 0;
  max-width: ${theme.dossier.column};
  margin: 0 auto 16px;
  overflow: hidden;
`;

export const CategoryLabel = styled.div`
  font-family: ${MONO};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.20em;
  text-transform: uppercase;
  color: ${theme.dossier.teal};
  margin-bottom: 10px;
`;

export const EstimateRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  padding: 13px 0;
  border-bottom: 1px solid ${theme.dossier.hairlineOnDark};

  &:last-of-type { border-bottom: none; }
`;

export const EstimateDesc = styled.span`
  font-size: 13px;
  color: ${theme.dossier.mutedOnDark};
`;

export const EstimateVal = styled.span`
  font-family: ${MONO};
  font-size: 13px;
  font-weight: 600;
  color: ${({ $highlight }) => $highlight ? theme.dossier.tealBright : theme.dossier.inkOnDark};
  text-align: right;
`;

export const EstimateValNote = styled.span`
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 400;
  color: ${theme.dossier.faintOnDark};
  margin-top: 2px;
`;

/* Premie-bandet bleedar till kortets kanter — en avslutning, inte en låda i lådan */
export const SavingBand = styled.div`
  background: ${theme.dossier.bg};
  border-top: 1px solid ${theme.dossier.hairlineOnDark};
  margin: 18px -24px 0;
  padding: 18px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;

  > div { text-align: right; }
`;

export const SavingLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: rgba(255,255,255,0.62);
`;

export const SavingCentral = styled.div`
  font-family: ${MONO};
  font-size: 16px;
  font-weight: 600;
  color: ${theme.dossier.tealBright};
  letter-spacing: -0.01em;
`;

export const SavingInterval = styled.div`
  font-family: ${MONO};
  font-size: 10.5px;
  color: rgba(255,255,255,0.46);
  margin-top: 3px;
`;

export const SourceNote = styled.div`
  font-size: 11px;
  color: ${theme.dossier.faintOnDark};
  margin: 12px 0 0;
  padding-bottom: 16px;
`;

// ── CTA ───────────────────────────────────────────────────────────────────────

export const CtaSection = styled.div`
  background: ${theme.dossier.bg};
  border-top: 1px solid rgba(255,255,255,0.07);
  padding: 72px 24px 60px;
  text-align: center;

  @media (min-width: 768px) {
    padding: 110px 24px 96px;
  }
`;

export const MethodologyNote = styled.p`
  font-size: 12px;
  color: rgba(255,255,255,0.46);
  line-height: 1.80;
  max-width: 360px;
  margin: 0 auto 52px;
`;

export const PrimaryCtaWrap = styled.div`
  margin-bottom: 10px;
`;

export const PrimaryCta = styled.a`
  display: block;
  max-width: 400px;
  margin-inline: auto;
  background: ${theme.dossier.ctaGradient};
  color: #fff;
  font-size: 17px;
  font-weight: 700;
  letter-spacing: 0.01em;
  white-space: nowrap;
  padding: 22px 32px;
  border-radius: 100px;
  text-align: center;
  text-decoration: none;
  box-shadow: ${theme.dossier.ctaShadow};
  box-sizing: border-box;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:active {
    transform: scale(0.97);
    box-shadow: 0 8px 24px rgba(29,176,154,0.20);
  }
`;

export const PrimaryCtaSub = styled.div`
  font-size: 12px;
  color: rgba(255,255,255,0.50);
  margin-top: 14px;
`;

export const CtaGap = styled.div`
  height: 36px;
`;

/* Intelligence på kall trafik är en viskning, inte en knapp —
   första kontaktytan har EN handling. */
export const SecondaryLink = styled.a`
  display: inline-block;
  font-size: 14px;
  font-weight: 500;
  color: rgba(255,255,255,0.55);
  text-decoration: none;
  border-bottom: 1px solid rgba(255,255,255,0.18);
  padding-bottom: 3px;
  transition: color 0.15s, border-color 0.15s;

  &:active {
    color: rgba(255,255,255,0.90);
    border-color: rgba(255,255,255,0.40);
  }
`;

export const SecondaryCtaSub = styled.div`
  font-size: 12px;
  color: rgba(255,255,255,0.38);
  margin-top: 12px;
`;

// ── Footer ────────────────────────────────────────────────────────────────────

export const PageFooter = styled.div`
  border-top: 1px solid rgba(255,255,255,0.06);
  padding: 22px 28px calc(22px + env(safe-area-inset-bottom, 0px));
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${theme.dossier.bg};
`;

export const FooterDomain = styled.span`
  font-family: ${MONO};
  font-size: 11px;
  color: rgba(255,255,255,0.32);
`;

export const FooterBrand = styled.span`
  font-family: ${MONO};
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.20em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.28);
`;

// ── Loading / Error ───────────────────────────────────────────────────────────

export const LoadingWrap = styled.div`
  min-height: 100vh;
  background: ${theme.dossier.bg};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
`;

export const Dots = styled.div`display: flex; gap: 8px;`;

export const Dot = styled.div`
  width: 8px; height: 8px;
  border-radius: 50%;
  background: ${theme.dossier.teal};
  animation: ${pulse} 1.2s ${({ $i }) => ($i ?? 0) * 0.2}s ease-in-out infinite;
`;

export const LoadingText = styled.div`
  font-size: 13px;
  color: rgba(255,255,255,0.32);
`;

export const ErrorWrap = styled.div`
  min-height: 100vh;
  background: ${theme.dossier.bg};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 28px;
  text-align: center;
`;

export const ErrorIcon = styled.div`
  font-size: 32px;
  margin-bottom: 20px;
  animation: ${fadeIn} 0.4s ease both;
`;

export const ErrorTitle = styled.h2`
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 24px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 12px;
`;

export const ErrorBody = styled.p`
  font-size: 14px;
  color: rgba(255,255,255,0.40);
  line-height: 1.65;
  max-width: 300px;
  margin: 0 0 28px;
`;

export const ErrorCta = styled.a`
  font-size: 15px;
  font-weight: 600;
  color: ${theme.dossier.teal};
  text-decoration: none;
  border-bottom: 1px solid rgba(29,176,154,0.3);
  padding-bottom: 2px;
`;
